'use client'

import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from 'react'
import { preload } from 'react-dom'
import { usePrefersReducedMotion } from '@/lib/usePrefersReducedMotion'
import { useTheme } from '@/lib/theme/useTheme'
import {
  EIGENFIELD_VARIANTS,
  type EigenfieldVariant,
  type TextBandFade,
} from '@roomtreatment/design'
import { createEigenfield, supportsEigenfield, type EigenfieldHandle } from './eigenfieldRenderer'

type Props = {
  /** Where the lines thin so text below stays legible, in screen fractions. */
  artFade?: TextBandFade
  /** Which superposition to draw. The landing page carries the field down its
   *  whole length and four copies of one picture reads as a tiled background;
   *  see EIGENFIELD_VARIANTS for what is allowed to differ and why.
   *
   *  Only the live shader honours this — the baked still is one picture, and
   *  always the hero's. That is the same asymmetry `artFade` already has, and it
   *  is safe for the same reason: nothing about legibility depends on either. */
  variant?: EigenfieldVariant
  /** Cap on the backing store's device-pixel ratio. Defaults to the
   *  renderer's own 2, which is right for a field that has a screen to
   *  itself. A section that runs one of these BEHIND live 3D is a different
   *  case — see the homepage's ResultSection. */
  maxPixelRatio?: number
  className?: string
}

/**
 * The generated acoustic field, as a background layer.
 *
 * ── Legibility is NOT this component's job ───────────────────────────────────
 * There are two implementations below: the live shader, and a baked still for
 * browsers without WebGL2. They do not thin their lines identically — the still
 * cannot respond to `artFade` at all. So nothing may depend on this component
 * for contrast. Text that sits over it gets its readability from a real element
 * in the tree that renders on every path (see `FieldPlate`), exactly as the app
 * does after shipping a headline that was invisible on one of its two
 * backgrounds while every test passed (mobile/gotchas.md #8).
 *
 * WebGL support is read via useSyncExternalStore — do NOT setState(failed) sync
 * inside an effect (react-hooks/set-state-in-effect / React Compiler).
 *
 * ── AI AGENT DIRECTIVE (PERFORMANCE & ASSETS) ────────────────────────────────
 * 1. WebGL initialisation MUST be deferred via `requestIdleCallback` (with a
 *    fallback timeout) so main-thread initial paint for LCP hero copy is never
 *    blocked by shader compilation or texture uploads.
 * 2. The `/eigenfield/*` textures are preloaded from this component (react-dom
 *    `preload`), so only pages that actually mount the field pay for them. They
 *    used to be <link rel="preload"> tags in the root layout, which made every
 *    route on the site, blog and tools included, download two hero textures.
 */
const DEFAULT_FADE: TextBandFade = { start: 0.45, end: 0.72, minAlpha: 0.1 }

/** Cap on how long we wait for idle before starting WebGL. Short enough
 *  that the field can fade in with the hero copy. */
export const EIGENFIELD_IDLE_TIMEOUT_MS = 300

/** Opacity fade after the first frame exists. Same spring as Reveal, shorter
 *  than `--rt-spring-ms` so the field arrives a beat before the headline
 *  finishes settling. */
export const EIGENFIELD_REVEAL_MS = 380

const subscribeNoop = () => () => {}

function useSupportsEigenfield(): boolean {
  return useSyncExternalStore(subscribeNoop, supportsEigenfield, () => true)
}

function fieldRevealStyle(ready: boolean, reducedMotion: boolean): CSSProperties {
  return {
    opacity: ready ? 1 : 0,
    transition: reducedMotion ? 'none' : `opacity ${EIGENFIELD_REVEAL_MS}ms var(--rt-ease-spring)`,
  }
}

/**
 * The baked still, re-lit for navy without a second asset: invert + a half-turn
 * of hue is lightness reflected with hue held (near enough for low-chroma line
 * art), and `screen` then drops the inverted paper — now near-black — onto the
 * theme's own paper, so only the lines survive. A stopgap for the fallback
 * path only; the proper fix is baking a dark still beside the light one.
 */
const STILL_DARK: CSSProperties = { filter: 'invert(1) hue-rotate(180deg)', mixBlendMode: 'screen' }

function EigenfieldStill({ className }: { className?: string }) {
  const reducedMotion = usePrefersReducedMotion()
  const [revealed, setRevealed] = useState(false)
  const ready = reducedMotion || revealed
  const hostRef = useRef<HTMLDivElement>(null)
  const theme = useTheme(hostRef)

  useEffect(() => {
    if (reducedMotion) return
    const id = window.requestAnimationFrame(() => setRevealed(true))
    return () => window.cancelAnimationFrame(id)
  }, [reducedMotion])

  return (
    <div
      ref={hostRef}
      aria-hidden
      className={className}
      data-eigenfield-ready={ready ? 'true' : 'false'}
      style={{ background: 'var(--rt-paper)' }}
    >
      <div
        data-eigenfield="still"
        className="h-full w-full"
        style={{
          backgroundImage: 'url(/eigenfield/bg-drift-15.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          ...(theme === 'dark' ? STILL_DARK : null),
          ...fieldRevealStyle(ready, reducedMotion),
        }}
      />
    </div>
  )
}

export default function Eigenfield({
  artFade = DEFAULT_FADE,
  variant = EIGENFIELD_VARIANTS.hero,
  maxPixelRatio,
  className,
}: Props) {
  preload('/eigenfield/eigenfield-abc.png', { as: 'image', type: 'image/png' })
  preload('/eigenfield/eigenfield-lut.png', { as: 'image', type: 'image/png' })
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const webglOk = useSupportsEigenfield()
  const [runtimeFailed, setRuntimeFailed] = useState(false)
  const [ready, setReady] = useState(false)
  const reducedMotion = usePrefersReducedMotion()
  // A flip rebuilds the handle (it is an effect dependency below): the ground
  // and the re-inked LUT are fixed for a handle's life, and a theme change is
  // rare enough that one shader compile is the honest price.
  const theme = useTheme(canvasRef)

  useEffect(() => {
    if (!webglOk) return
    const canvas = canvasRef.current
    if (!canvas) return

    let handle: EigenfieldHandle | null = null
    let disposed = false

    const reducedMotionPref = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const initWebGL = () => {
      if (disposed) return
      createEigenfield(canvas, { artFade, variant, maxPixelRatio, theme, reducedMotion: reducedMotionPref })
        .then((created) => {
          if (disposed) {
            created.destroy()
            return
          }
          handle = created
          // First frame is already in the buffer (`renderOnce(0)` before
          // createEigenfield returns). Reveal now so the lines fade in
          // instead of popping onto blank paper.
          setReady(true)
          if (!reducedMotionPref) created.start()
        })
        .catch(() => setRuntimeFailed(true))
    }

    // Yield one paint so LCP text can commit, then start. The idle timeout is
    // the latest we will wait — not a guaranteed delay.
    let idleId: number | ReturnType<typeof setTimeout> | null = null
    const schedule = () => {
      if (idleId !== null || disposed) return
      idleId = typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(initWebGL, { timeout: EIGENFIELD_IDLE_TIMEOUT_MS })
        : setTimeout(initWebGL, 50)
    }

    // One observer, two jobs.
    //
    // Before there is a context: it decides WHEN to make one. The landing page
    // runs the field behind four separate sections, and creating them all at
    // mount means four shader compiles and four WebGL contexts on a marketing
    // page a visitor may never scroll — against a browser budget of roughly
    // sixteen, shared with the two the showcase's rooms already take. A section
    // that is never approached never costs anything. The hero is intersecting at
    // mount, so nothing about its timing changes.
    //
    // After there is one: it pauses the rAF loop off-screen. It does NOT destroy
    // the context — that blanks a section after a scroll-away.
    //
    // `rootMargin` so a section starts compiling while it is still a screen out
    // and is already drawing by the time it arrives.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) schedule()
        if (!handle) return
        if (entry.isIntersecting && !document.hidden) handle.start()
        else handle.stop()
      },
      { threshold: 0, rootMargin: '100% 0px' },
    )
    observer.observe(canvas)

    const onVisibility = () => {
      if (!handle) return
      if (document.hidden) handle.stop()
      else handle.start()
    }
    document.addEventListener('visibilitychange', onVisibility)

    const onContextLost = () => {
      if (!disposed) setRuntimeFailed(true)
    }
    canvas.addEventListener('webglcontextlost', onContextLost)

    const resizeObserver = new ResizeObserver(() => handle?.renderOnce(performance.now() / 1000))
    resizeObserver.observe(canvas)

    return () => {
      disposed = true
      if (idleId !== null) {
        if (typeof window.cancelIdleCallback === 'function' && typeof idleId === 'number') {
          window.cancelIdleCallback(idleId)
        } else {
          clearTimeout(idleId as ReturnType<typeof setTimeout>)
        }
      }
      observer.disconnect()
      resizeObserver.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      canvas.removeEventListener('webglcontextlost', onContextLost)
      handle?.destroy()
    }
  }, [artFade, variant, maxPixelRatio, theme, webglOk])

  if (!webglOk || runtimeFailed) {
    return <EigenfieldStill className={className} />
  }

  // Host carries the caller's layout/mask/opacity. The canvas fills it and
  // fades 0 → 1 after the first frame, so a landing `opacity-[0.42]` still
  // means 42% of the field, not 42% of a pop.
  return (
    <div className={className} data-eigenfield-ready={ready ? 'true' : 'false'}>
      <canvas
        ref={canvasRef}
        aria-hidden
        data-eigenfield="live"
        className="h-full w-full"
        style={{
          background: 'var(--rt-paper)',
          ...fieldRevealStyle(ready, reducedMotion),
        }}
      />
    </div>
  )
}
